/**
 * SPCB Order Summary Block
 *
 * Renders a Blocks-style order summary list using Store API data.
 *
 * @package SuperPymeCheckoutBlocks
 * @since 1.14.0
 */

import CartItemQuantityControl from './CartItemQuantityControl';
import { decodeEntities } from '../utils/html';

const { createElement, RawHTML, useEffect, useRef, Fragment, Component } = wp.element;
const { useSelect } = wp.data;
const { __, _n, sprintf } = wp.i18n || {};

const CART_STORE_KEY = 'wc/store/cart';

/**
 * Error Boundary for Order Summary
 *
 * Catches JavaScript errors in the Order Summary component tree
 * and displays a fallback UI instead of crashing the entire checkout.
 */
class OrderSummaryErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        // Log to console for debugging
        console.error('[SPCB] Order Summary Error:', error, errorInfo);

        // Log via SPCB logging system if available
        if (window.spcbQuantityData?.restUrl) {
            try {
                const blob = new Blob([JSON.stringify({
                    event: 'error_boundary_catch',
                    error: error?.message || String(error),
                    stack: error?.stack?.slice(0, 500),
                    component: errorInfo?.componentStack?.slice(0, 300),
                    timestamp: Date.now()
                })], { type: 'application/json' });
                navigator.sendBeacon(window.spcbQuantityData.restUrl, blob);
            } catch (e) {
                // Ignore logging errors
            }
        }
    }

    render() {
        if (this.state.hasError) {
            // Fallback UI - minimal, doesn't break checkout
            return createElement('div', {
                className: 'wc-block-components-order-summary spcb-error-fallback',
                style: {
                    padding: '16px',
                    textAlign: 'center',
                    color: '#666',
                    fontSize: '14px'
                }
            }, [
                createElement('p', { key: 'msg', style: { margin: '0 0 8px' } },
                    __ ? __('Error al cargar el resumen.', 'superpyme-checkout-blocks') : 'Error al cargar el resumen.'
                ),
                createElement('button', {
                    key: 'btn',
                    type: 'button',
                    onClick: () => window.location.reload(),
                    style: {
                        padding: '8px 16px',
                        cursor: 'pointer',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        background: '#f7f7f7'
                    }
                }, __ ? __('Recargar página', 'superpyme-checkout-blocks') : 'Recargar página')
            ]);
        }
        return this.props.children;
    }
}

function applyAutop(text) {
    if (!text) {
        return '';
    }
    if (typeof window.wp?.autop === 'function') {
        return window.wp.autop(text);
    }
    if (typeof window.wp?.autop?.autop === 'function') {
        return window.wp.autop.autop(text);
    }
    return `<p>${text}</p>`;
}

function countText(text, countType) {
    if (!text) {
        return 0;
    }
    if (typeof window.wp?.wordcount?.count === 'function') {
        return window.wp.wordcount.count(text, countType);
    }
    return text.trim().split(/\s+/).length;
}

function getFirstParagraph(source) {
    const paragraphIndex = source.indexOf('</p>');
    if (paragraphIndex === -1) {
        return source;
    }
    return source.substr(0, paragraphIndex + 4);
}

function removeTags(htmlString) {
    const tagsRegExp = /<\/?[a-z][^>]*?>/gi;
    return htmlString.replace(tagsRegExp, '');
}

function appendMoreText(text, moreText) {
    return text.replace(/[\s|\.\,]+$/i, '') + moreText;
}

function trimWords(text, maxLength, moreText = '&hellip;', useAutop = true) {
    const textToTrim = removeTags(text);
    const trimmedText = textToTrim.split(' ').splice(0, maxLength).join(' ');
    if (trimmedText === textToTrim) {
        return useAutop ? applyAutop(textToTrim) : textToTrim;
    }
    if (!useAutop) {
        return appendMoreText(trimmedText, moreText);
    }
    return applyAutop(appendMoreText(trimmedText, moreText));
}

function trimCharacters(text, maxLength, includeSpaces = true, moreText = '&hellip;', useAutop = true) {
    const textToTrim = removeTags(text);
    const trimmedText = textToTrim.slice(0, maxLength);
    if (trimmedText === textToTrim) {
        return useAutop ? applyAutop(textToTrim) : textToTrim;
    }
    if (includeSpaces) {
        return applyAutop(appendMoreText(trimmedText, moreText));
    }
    const matchSpaces = trimmedText.match(/([\s]+)/g);
    const spaceCount = matchSpaces ? matchSpaces.length : 0;
    const trimmedTextExcludingSpaces = textToTrim.slice(0, maxLength + spaceCount);
    if (!useAutop) {
        return appendMoreText(trimmedTextExcludingSpaces, moreText);
    }
    return applyAutop(appendMoreText(trimmedTextExcludingSpaces, moreText));
}

function getBlocksWordCountType() {
    if (window.wcSettings?.getSetting) {
        const blocksConfig = window.wcSettings.getSetting('wcBlocksConfig', {});
        if (blocksConfig?.wordCountType) {
            return blocksConfig.wordCountType;
        }
    }
    return 'words';
}

function generateSummary(source, maxLength = 15, countType = 'words') {
    const sourceWithParagraphs = applyAutop(source);
    const sourceWordCount = countText(sourceWithParagraphs, countType);
    if (sourceWordCount <= maxLength) {
        return sourceWithParagraphs;
    }

    const firstParagraph = getFirstParagraph(sourceWithParagraphs);
    const firstParagraphWordCount = countText(firstParagraph, countType);
    if (firstParagraphWordCount <= maxLength) {
        return firstParagraph;
    }

    if (countType === 'words') {
        return trimWords(firstParagraph, maxLength);
    }

    return trimCharacters(firstParagraph, maxLength, countType === 'characters_including_spaces');
}

function buildSummaryText(source, maxLength = 50) {
    if (!source) {
        return '';
    }
    const normalized = decodeEntities(removeTags(source))
        .replace(/\s+/g, ' ')
        .trim();
    if (!normalized) {
        return '';
    }
    const periodIndex = normalized.indexOf('.');
    if (periodIndex !== -1 && periodIndex < maxLength) {
        return normalized.slice(0, periodIndex + 1).trim();
    }
    if (normalized.length <= maxLength) {
        return normalized;
    }
    return `${normalized.slice(0, maxLength).trim()}...`;
}

function mustContain(value, needle) {
    return typeof value === 'string' && value.includes(needle);
}

/**
 * Get translated low stock text with fallback to Spanish
 *
 * @param {number} remaining - Number of items remaining in stock
 * @returns {string} - Translated/localized low stock message
 */
function getLowStockText(remaining) {
    // Try to use WooCommerce Blocks translation
    if (typeof sprintf === 'function' && typeof __ === 'function') {
        try {
            const translated = sprintf(
                __('%d left in stock', 'woo-gutenberg-products-block'),
                remaining
            );
            // Check if translation worked (didn't return the original English)
            if (translated && !translated.includes('left in stock')) {
                return translated;
            }
        } catch (e) {
            // Ignore translation errors
        }
    }

    // Fallback to Spanish (for sites without WC Blocks translations)
    if (remaining === 1) {
        return 'Queda 1 en stock';
    }
    return `Quedan ${remaining} en stock`;
}

/**
 * Get placeholder image URL with multiple fallback sources
 *
 * @returns {string} - Placeholder image URL
 */
function getPlaceholderImageSrc() {
    // 1. Try wcSettings.placeholderImgSrc (WC core)
    if (window.wcSettings?.getSetting) {
        const wcPlaceholder = window.wcSettings.getSetting('placeholderImgSrc', '');
        if (wcPlaceholder) return wcPlaceholder;
    }

    // 2. Try wcSettings.wcBlocksConfig.placeholderImgSrc
    if (window.wcSettings?.getSetting) {
        const blocksConfig = window.wcSettings.getSetting('wcBlocksConfig', {});
        if (blocksConfig?.placeholderImgSrc) return blocksConfig.placeholderImgSrc;
    }

    // 3. Try spcbQuantityData (our plugin)
    if (window.spcbQuantityData?.placeholderImgSrc) {
        return window.spcbQuantityData.placeholderImgSrc;
    }

    // 4. Fallback: construct WooCommerce standard placeholder URL
    const homeUrl = window.wcSettings?.getSetting?.('homeUrl', '') || '';
    if (homeUrl) {
        return `${homeUrl}/wp-content/uploads/woocommerce-placeholder.png`;
    }

    // 5. Last resort: relative path
    return '/wp-content/uploads/woocommerce-placeholder.png';
}

function applyCheckoutFilter({ filterName, defaultValue, extensions, arg, validation }) {
    const checkoutApi = window.wc?.blocksCheckout;
    if (checkoutApi?.applyCheckoutFilter) {
        const filtered = checkoutApi.applyCheckoutFilter({
            filterName,
            defaultValue,
            extensions,
            arg,
            validation
        });
        if (typeof validation === 'function' && !validation(filtered)) {
            return defaultValue;
        }
        return filtered;
    }

    if (checkoutApi?.__experimentalApplyCheckoutFilter) {
        const filtered = checkoutApi.__experimentalApplyCheckoutFilter({
            filterName,
            defaultValue,
            extensions,
            arg,
            validation
        });
        if (typeof validation === 'function' && !validation(filtered)) {
            return defaultValue;
        }
        return filtered;
    }

    return defaultValue;
}

function getDisplayPricesIncludingTax() {
    if (window.wcSettings?.getSetting) {
        return window.wcSettings.getSetting('displayCartPricesIncludingTax', false);
    }
    return false;
}

function parseAmount(value) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
}

function formatMoney(amount, currency) {
    if (amount === null || amount === undefined || !currency) {
        return '';
    }

    const minorUnit = Number(currency.currency_minor_unit ?? 0);
    const divisor = Math.pow(10, minorUnit);
    const value = amount / divisor;
    const fixed = value.toFixed(minorUnit);
    const parts = fixed.split('.');
    const thousandSeparator = currency.currency_thousand_separator || '.';
    const decimalSeparator = currency.currency_decimal_separator || ',';
    const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);
    const decimalPart = parts[1];
    const number = decimalPart !== undefined ? `${integerPart}${decimalSeparator}${decimalPart}` : integerPart;

    if (currency.price_format && currency.currency_symbol) {
        return currency.price_format
            .replace('%1$s', currency.currency_symbol)
            .replace('%2$s', number);
    }

    const prefix = currency.currency_prefix ?? currency.currency_symbol ?? '';
    const suffix = currency.currency_suffix ?? '';
    return `${prefix}${number}${suffix}`;
}

function getQuantityLabel(quantity) {
    if (typeof _n === 'function' && typeof sprintf === 'function') {
        return sprintf(_n('%d item', '%d items', quantity, 'woo-gutenberg-products-block'), quantity);
    }
    return `${quantity} ${quantity === 1 ? 'artículo' : 'artículos'}`;
}

function buildItemTotal(totals, prices) {
    if (!totals) {
        return '';
    }

    const lineSubtotal = parseAmount(totals.line_subtotal);
    const lineSubtotalTax = parseAmount(totals.line_subtotal_tax);
    const includeTax = getDisplayPricesIncludingTax();
    const totalValue = includeTax && lineSubtotalTax !== null && lineSubtotal !== null
        ? lineSubtotal + lineSubtotalTax
        : lineSubtotal;

    return formatMoney(totalValue, totals.currency_code ? totals : prices);
}

function buildUnitPrice(prices) {
    if (!prices) {
        return { price: '', regularPrice: '', isDiscounted: false };
    }

    const priceValue = parseAmount(prices.price);
    const regularValue = parseAmount(prices.regular_price);
    const price = formatMoney(priceValue, prices);
    const regularPrice = formatMoney(regularValue, prices);
    const isDiscounted = regularValue !== null && priceValue !== null && regularValue > priceValue;

    return { price, regularPrice, isDiscounted };
}

function applyPriceFormat(price, format) {
    if (!price) {
        return '';
    }
    if (typeof format !== 'string') {
        return price;
    }
    if (!mustContain(format, '<price/>')) {
        return price;
    }
    return format.replace('<price/>', price);
}

function toParamCase(value) {
    if (!value) {
        return '';
    }
    return String(value)
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}

function renderProductDetails(details) {
    if (!Array.isArray(details)) {
        return null;
    }
    const visible = details.filter((detail) => !detail?.hidden);
    if (!visible.length) {
        return null;
    }

    return createElement('ul', { className: 'wc-block-components-product-details' },
        visible.map((detail) => {
            const name = detail?.key || detail?.name || '';
            const className = detail?.className || (name ? `wc-block-components-product-details__${toParamCase(name)}` : '');
            const rawValue = detail?.display || detail?.value || '';
            const value = decodeEntities(rawValue);
            const label = decodeEntities(name);
            const key = `${name}-${rawValue}`;

            return createElement('li', { key, className }, [
                name
                    ? createElement(Fragment, { key: 'label' }, [
                        createElement('span', {
                            key: 'name',
                            className: 'wc-block-components-product-details__name'
                        }, `${label}:`),
                        ' '
                    ])
                    : null,
                createElement('span', {
                    key: 'value',
                    className: 'wc-block-components-product-details__value'
                }, value)
            ]);
        })
    );
}

function renderProductBadge({ className, text }) {
    if (!text) {
        return null;
    }
    return createElement('div', {
        className: `wc-block-components-product-badge ${className || ''}`.trim()
    }, text);
}

function findOrderSummaryContent(rootElement) {
    if (!rootElement) {
        return null;
    }

    const scope = rootElement.closest('.wc-block-components-sidebar, .wc-block-checkout__actions')
        || document;
    const summaryBlock = scope.querySelector('.wp-block-woocommerce-checkout-order-summary-block');
    if (!summaryBlock) {
        return null;
    }

    return summaryBlock.querySelector('.wc-block-components-checkout-order-summary__content')
        || summaryBlock.querySelector('.checkout-order-summary-block-fill');
}

function insertAfter(target, node) {
    if (!target || !node || !target.parentNode) {
        return false;
    }
    if (target.nextSibling === node) {
        return false;
    }
    target.parentNode.insertBefore(node, target.nextSibling);
    return true;
}

function normalizeOrderSummaryLayout(rootElement) {
    const orderSummary = rootElement?.closest?.('.wp-block-woocommerce-checkout-order-summary-block');
    if (!orderSummary) {
        return false;
    }

    const title = orderSummary.querySelector(':scope > .wc-block-components-checkout-order-summary__title');
    const content = orderSummary.querySelector(':scope > .wc-block-components-checkout-order-summary__content')
        || orderSummary.querySelector(':scope > .checkout-order-summary-block-fill');
    const coupon = orderSummary.querySelector(':scope > .wp-block-woocommerce-checkout-order-summary-coupon-form-block');
    const totalsBlock = orderSummary.querySelector(':scope > .wp-block-woocommerce-checkout-order-summary-totals-block');
    const totalWrapper = Array.from(
        orderSummary.querySelectorAll(':scope > .wc-block-components-totals-wrapper')
    ).find((wrapper) => wrapper.querySelector('.wc-block-components-totals-footer-item'));
    const benefits = orderSummary.querySelector('#spcb-trust-benefits');

    if (title && content) {
        insertAfter(title, content);
    }

    if (content && coupon) {
        insertAfter(content, coupon);
    } else if (title && coupon) {
        insertAfter(title, coupon);
    }

    if (coupon && totalsBlock) {
        insertAfter(coupon, totalsBlock);
    } else if (content && totalsBlock) {
        insertAfter(content, totalsBlock);
    }

    if (totalsBlock && totalWrapper) {
        insertAfter(totalsBlock, totalWrapper);
    } else if (coupon && totalWrapper) {
        insertAfter(coupon, totalWrapper);
    }

    if (totalWrapper && benefits) {
        insertAfter(totalWrapper, benefits);
    } else if (totalsBlock && benefits) {
        insertAfter(totalsBlock, benefits);
    } else if (coupon && benefits) {
        insertAfter(coupon, benefits);
    }

    return true;
}

function ensureSummaryPlacement(rootElement) {
    const content = findOrderSummaryContent(rootElement);
    if (!content) {
        return false;
    }

    let wrapper = content.querySelector('.wp-block-spcb-checkout-order-summary-cart-items-block');
    if (!wrapper) {
        wrapper = document.createElement('div');
        wrapper.className = 'wp-block-spcb-checkout-order-summary-cart-items-block wc-block-components-totals-wrapper';
        const firstChild = content.firstElementChild;
        if (firstChild) {
            content.insertBefore(wrapper, firstChild);
        } else {
            content.appendChild(wrapper);
        }
    }

    if (rootElement.parentNode !== wrapper) {
        wrapper.appendChild(rootElement);
    }

    normalizeOrderSummaryLayout(rootElement);

    return true;
}

function OrderSummaryItem({ cartItem, cartData }) {
    if (!cartItem) {
        return null;
    }

    const {
        key,
        name,
        quantity,
        images = [],
        prices,
        totals,
        short_description: shortDescription,
        description: fullDescription,
        item_data: itemData = [],
        variation = [],
        show_backorder_badge: showBackorderBadge,
        low_stock_remaining: lowStockRemaining,
        extensions
    } = cartItem;

    const image = images.length ? images[0] : null;
    const arg = {
        context: 'summary',
        cartItem,
        cart: cartData || {}
    };
    const filteredName = applyCheckoutFilter({
        filterName: 'itemName',
        defaultValue: name,
        extensions,
        arg
    });
    const nameText = decodeEntities(name);
    const filteredNameText = typeof filteredName === 'string'
        ? decodeEntities(removeTags(filteredName))
        : nameText;
    const displayName = typeof filteredName === 'string'
        ? decodeEntities(filteredName)
        : filteredName;
    const nameContent = typeof filteredName === 'string' && filteredName.includes('<')
        ? createElement(RawHTML, { key: 'name-html' }, filteredName)
        : displayName;
    const summarySource = shortDescription || fullDescription || '';
    const summaryText = buildSummaryText(summarySource, 50);
    const unitPriceFormat = applyCheckoutFilter({
        filterName: 'subtotalPriceFormat',
        defaultValue: '<price/>',
        extensions,
        arg,
        validation: (value) => mustContain(value, '<price/>')
    });
    const totalPriceFormat = applyCheckoutFilter({
        filterName: 'cartItemPrice',
        defaultValue: '<price/>',
        extensions,
        arg,
        validation: (value) => mustContain(value, '<price/>')
    });
    const cartItemClass = applyCheckoutFilter({
        filterName: 'cartItemClass',
        defaultValue: '',
        extensions,
        arg
    });
    const showRemoveItemLink = applyCheckoutFilter({
        filterName: 'showRemoveItemLink',
        defaultValue: true,
        extensions,
        arg
    });
    const { price, regularPrice, isDiscounted } = buildUnitPrice(prices);
    const formattedPrice = applyPriceFormat(price, unitPriceFormat);
    const formattedRegularPrice = applyPriceFormat(regularPrice, unitPriceFormat);
    const totalPrice = applyPriceFormat(buildItemTotal(totals, prices), totalPriceFormat);
    const resolvedPlaceholder = getPlaceholderImageSrc();
    const imageSrc = image?.thumbnail || image?.src || '';
    const imageAlt = decodeEntities(image?.alt || '') || (typeof displayName === 'string' ? displayName : filteredNameText) || '';
    const backorderBadge = showBackorderBadge
        ? renderProductBadge({
            className: 'wc-block-components-product-backorder-badge',
            text: __ ? __('Available on backorder', 'woo-gutenberg-products-block') : 'Available on backorder'
        })
        : null;
    const lowStockBadge = lowStockRemaining
        ? renderProductBadge({
            className: 'wc-block-components-product-low-stock-badge',
            text: getLowStockText(lowStockRemaining)
        })
        : null;
    const variationDetails = Array.isArray(variation)
        ? variation.map(({ attribute = '', value }) => ({ key: attribute, value }))
        : [];

    return createElement('div', {
        className: `wc-block-components-order-summary-item spcb-item-${key || ''} ${cartItemClass || ''}`.trim()
    }, [
        createElement('div', { key: 'image', className: 'wc-block-components-order-summary-item__image' }, [
            createElement('div', { key: 'qty', className: 'wc-block-components-order-summary-item__quantity' }, [
                createElement('span', { key: 'qty-value', 'aria-hidden': 'true' }, String(quantity)),
                createElement('span', { key: 'qty-label', className: 'screen-reader-text' }, getQuantityLabel(quantity))
            ]),
            imageSrc
                ? createElement('img', {
                    key: 'img',
                    src: imageSrc,
                    alt: imageAlt,
                    width: image?.thumbnail_width || 48,
                    height: image?.thumbnail_height || 48
                })
                : resolvedPlaceholder
                    ? createElement('img', {
                        key: 'img-placeholder',
                        src: resolvedPlaceholder,
                        alt: '',
                        width: 48,
                        height: 48
                    })
                : createElement('span', { key: 'img-placeholder', className: 'wc-block-components-order-summary-item__image-placeholder' })
        ]),
        createElement('div', { key: 'description', className: 'wc-block-components-order-summary-item__description' }, [
            createElement('h3', { key: 'name', className: 'wc-block-components-product-name' }, nameContent),
            createElement('span', {
                key: 'price',
                className: 'wc-block-components-order-summary-item__individual-prices price wc-block-components-product-price'
            }, [
                isDiscounted
                    ? createElement('span', { key: 'price-regular-label', className: 'screen-reader-text' }, 'Precio anterior:')
                    : null,
                isDiscounted
                    ? createElement('del', {
                        key: 'price-regular',
                        className: 'wc-block-components-product-price__regular wc-block-components-order-summary-item__regular-individual-price'
                    }, formattedRegularPrice || regularPrice)
                    : null,
                isDiscounted
                    ? createElement('span', { key: 'price-sale-label', className: 'screen-reader-text' }, 'Precio con descuento:')
                    : null,
                createElement(isDiscounted ? 'ins' : 'span', {
                    key: 'price-current',
                    className: isDiscounted
                        ? 'wc-block-components-product-price__value is-discounted wc-block-components-order-summary-item__individual-price'
                        : 'wc-block-components-product-price__value wc-block-components-order-summary-item__individual-price'
                }, formattedPrice || price)
            ]),
            backorderBadge || lowStockBadge
                ? createElement(Fragment, { key: 'badges' }, [backorderBadge, lowStockBadge].filter(Boolean))
                : null,
            createElement('div', { key: 'metadata', className: 'wc-block-components-product-metadata' }, [
                summaryText
                    ? createElement('div', { key: 'desc', className: 'wc-block-components-product-metadata__description' }, [
                        createElement('p', { key: 'desc-text' }, summaryText)
                    ])
                    : null,
                renderProductDetails(itemData),
                renderProductDetails(variationDetails)
            ]),
            createElement('div', {
                key: 'qty-container',
                className: 'spcb-qty-control-container',
                'data-item-key': key
            }, createElement(CartItemQuantityControl, {
                cartItem,
                itemKey: key,
                showRemoveItemLink: showRemoveItemLink !== false
            }))
        ]),
        createElement('span', { key: 'sr-total', className: 'screen-reader-text' }, `Precio total para ${quantity} ${quantity === 1 ? 'artículo' : 'artículos'} de ${typeof displayName === 'string' ? displayName : filteredNameText}: ${totalPrice}`),
        createElement('div', { key: 'total', className: 'wc-block-components-order-summary-item__total-price', 'aria-hidden': 'true' }, [
            createElement('span', { key: 'price', className: 'price wc-block-components-product-price' }, [
                createElement('span', {
                    key: 'amount',
                    className: 'wc-block-formatted-money-amount wc-block-components-formatted-money-amount wc-block-components-product-price__value'
                }, totalPrice)
            ])
        ])
    ]);
}

function OrderSummaryBlockInner({ className = '' }) {
    const rootRef = useRef(null);
    const { cartItems, cartData } = useSelect((select) => {
        const store = select(CART_STORE_KEY);
        if (!store) {
            return { cartItems: [], cartData: {} };
        }

        const cartData = typeof store.getCartData === 'function'
            ? store.getCartData()
            : (typeof store.getCart === 'function' ? store.getCart() : null);

        const items = cartData?.items || [];
        const normalizedItems = items.map((item) => ({
            ...item,
            quantity_limits: item.quantity_limits ? { ...item.quantity_limits } : item.quantity_limits
        }));

        return {
            cartItems: normalizedItems,
            cartData: cartData || {}
        };
    }, []);

    useEffect(() => {
        window.spcbOrderSummaryMounted = true;
        document.querySelectorAll('.wp-block-woocommerce-checkout-order-summary-cart-items-block').forEach((coreBlock) => {
            coreBlock.style.display = 'none';
            coreBlock.dataset.spcbHidden = 'true';
        });

        let attempts = 0;
        let timer;
        const maxAttempts = 20;

        const tryPlace = () => {
            if (!rootRef.current) {
                return;
            }
            if (ensureSummaryPlacement(rootRef.current)) {
                return;
            }
            attempts += 1;
            if (attempts < maxAttempts) {
                timer = setTimeout(tryPlace, 250);
            }
        };

        tryPlace();

        const observer = new MutationObserver(() => {
            if (!rootRef.current) {
                return;
            }
            if (ensureSummaryPlacement(rootRef.current)) {
                observer.disconnect();
            }
        });

        observer.observe(document.body, { childList: true, subtree: true });

        return () => {
            if (timer) {
                clearTimeout(timer);
            }
            observer.disconnect();
        };
    }, []);

    return createElement('div', {
        className: `wc-block-components-order-summary is-large ${className}`.trim(),
        ref: rootRef
    }, createElement('div', { className: 'wc-block-components-order-summary__content' },
        cartItems.map((cartItem) => createElement(OrderSummaryItem, {
            key: cartItem.key,
            cartItem,
            cartData
        }))
    ));
}

/**
 * Wrapped Order Summary Block with Error Boundary
 *
 * Ensures errors in the Order Summary don't crash the entire checkout.
 */
export default function OrderSummaryBlock(props) {
    return createElement(OrderSummaryErrorBoundary, null,
        createElement(OrderSummaryBlockInner, props)
    );
}
